import { Fragment } from 'react';
import { clsx } from 'clsx';

import styles from './LogoBar.module.scss';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Img } from '../../display/Img';
import {
  AsanaIcon,
  AsanaIconMobile,
  DefaultCompanies,
  DefaultCompany,
  DefaultLogoIconType,
  DropboxIcon,
  DropboxIconMobile,
  MetaIcon,
  MetaIconMobile,
  ShopifyIcon,
  ShopifyIconMobile,
  SlackIcon,
  SlackIconMobile,
} from './logoIcons';

type ItemProps = {
  logoUrl: string;
  companyName: string;
};

export type Props = {
  /**
   * The title of the `LogoBar`.
   */
  title: React.ReactNode;
  /**
   * Array of items to render as images below the title.
   */
  items: Array<ItemProps>;
  /**
   * Size of the logos. This value will be the width and height of
   * the logo container.
   */
  size?: number;
  /** Optional prop for overriding styles. */
  className?: string;
};

const DefaultLogo = (props: DefaultLogoIconType) => {
  return (
    <span
      className={clsx(styles.defaultLogo, 'logobar-defaultLogo')}
      style={{
        '--_zds-logobar-logosize': props.size ? `${props.size}px` : '',
      }}
      dangerouslySetInnerHTML={{ __html: props.svg }}
    />
  );
};

const DefaultLogoIcons: Record<
  DefaultCompany,
  { mobile: DefaultLogoIconType; desktop: DefaultLogoIconType }
> = {
  Asana: {
    mobile: AsanaIconMobile,
    desktop: AsanaIcon,
  },
  Dropbox: {
    mobile: DropboxIconMobile,
    desktop: DropboxIcon,
  },
  Meta: {
    mobile: MetaIconMobile,
    desktop: MetaIcon,
  },
  Shopify: {
    mobile: ShopifyIconMobile,
    desktop: ShopifyIcon,
  },
  Slack: {
    mobile: SlackIconMobile,
    desktop: SlackIcon,
  },
};

export const LogoBar = (_props: Props) => {
  const { title, items, size, className } = _props;

  return (
    <div className={clsx(styles.root, className)} data-zds>
      <HeadingLevel>
        <Heading>
          <div className={clsx(styles.title, 'logobar-title')} data-zds>
            {title}
          </div>
        </Heading>
      </HeadingLevel>
      <ul
        className={clsx(styles.logosContainer, 'logobar-logosContainer')}
        data-zds
      >
        {items && items.length > 0
          ? items.map(({ logoUrl, companyName }, i) => (
              <li className={styles.logoContainer} key={i + logoUrl} data-zds>
                <Img
                  alt={companyName}
                  borderRadius="none"
                  src={logoUrl}
                  height={size}
                  width={size}
                />
              </li>
            ))
          : DefaultCompanies.map((company) => (
              <Fragment key={company}>
                <li
                  className={clsx(
                    styles.logoContainer,
                    styles.desktopLogoContainer
                  )}
                  data-zds
                >
                  <DefaultLogo
                    {...DefaultLogoIcons[company].desktop}
                    size={size}
                  />
                </li>
                <li
                  className={clsx(
                    styles.logoContainer,
                    styles.mobileLogoContainer
                  )}
                  data-zds
                >
                  <DefaultLogo
                    {...DefaultLogoIcons[company].mobile}
                    size={size}
                  />
                </li>
              </Fragment>
            ))}
      </ul>
    </div>
  );
};
